import Vue from "vue";
import { mapGetters } from "vuex";
import { LoadingSpinner } from "@/components";
import { themeHelper } from "@/components/mixins/themeHelper";
import { constants } from "@/const";
import DiagramViewer from "@/components/PartDiagramSearch/DiagramViewer/DiagramViewer.vue";
import DiagramPartsList from "@/components/PartDiagramSearch/DiagramPartsList/DiagramPartsList.vue";
import PartDetailRow from "@/components/PartDiagramSearch/PartDetailRow/PartDetailRow.vue";
import PaginationText from "@/components/Elements/PaginationText.vue";
import Pagination from "@/components/Elements/Pagination/Pagination.vue";
import PartSearchFacets from "@/components/SubMenus/PartSearchFacets.vue";
import { DnRouterLink } from "@/components/Elements";

/**
 * PartSearchResultsPanel – Renders the right-hand results area of the Part
 * Diagram page. Supports two layout modes:
 *
 *  1. **Diagram mode** (`showDiagram = true`) – side-by-side DiagramViewer
 *     and DiagramPartsList.
 *  2. **List mode** (`showDiagram = false`) – traditional paginated
 *     PartDetailRow list with sort, compact/detailed toggle, and alt-part legend.
 */
export default Vue.extend({
    name: "PartSearchResultsPanel",
    components: {
        LoadingSpinner,
        DiagramViewer,
        DiagramPartsList,
        PartDetailRow,
        PaginationText,
        Pagination,
        PartSearchFacets,
        DnRouterLink,
    },
    mixins: [
        themeHelper
    ],
    props: {
        /** Whether the results are still loading. */
        loading: {
            type: Boolean,
            default: false
        },
        /** Search results object containing `Parts`, `Facets`, etc. */
        searchResults: {
            type: Object,
            default: () => ({})
        },
        /** Whether any returned parts have alternate-part tabs. */
        hasAltParts: {
            type: Boolean,
            default: false
        },
        /** URL (or data-URI) of the diagram image. */
        diagramUrl: {
            type: String,
            default: ""
        },
        /** Alt text for the diagram image. */
        diagramAltText: {
            type: String,
            default: ""
        },
        /** Whether search results contain parts. */
        hasResults: {
            type: Boolean,
            default: false
        },
        /** When true, renders diagram+cards layout instead of list layout. */
        showDiagram: {
            type: Boolean,
            default: true
        },
        /** Total number of parts across all pages. */
        totalParts: {
            type: Number,
            default: 0
        },
        /** Number of parts per page in list mode. */
        partsPerPage: {
            type: Number,
            default: 10
        },
        /** Initial page number (from deep-link or parent). */
        initialPageNumber: {
            type: Number,
            default: 1
        },
        /** Currently selected facets from the parent page. */
        selectedFacets: {
            type: Object,
            default: () => ({})
        },
        /** Active search term text. */
        searchTerm: {
            type: String,
            default: ""
        },
        /** Whether OemManuals mode is active (client-side pagination). */
        isDiagramSearch: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters([
            "getContentItem",
            "partSearchDefaultSortValue",
            "tmsCompactViewDefaultValue"
        ]),
        /** Effective total: in OemManuals mode use the full Parts array length. */
        effectiveTotalParts(): number {
            if (this.isDiagramSearch && this.searchResults?.Parts) {
                return this.searchResults.Parts.length;
            }
            return this.totalParts;
        },
        /** Total pages based on effective total. */
        effectiveTotalPages(): number {
            return Math.ceil(this.effectiveTotalParts / this.partsPerPage);
        },
        /** Parts to display: client-side slice for OemManuals, full array for TMS (server-paginated). */
        displayedParts(): any[] {
            if (!this.searchResults?.Parts) return [];
            if (this.isDiagramSearch) {
                const start = (this.pageNumber - 1) * this.partsPerPage;
                return this.searchResults.Parts.slice(start, start + this.partsPerPage);
            }
            return this.searchResults.Parts;
        },
    },
    data: () => ({
        /** Whether the condensed (compact) part view is active. */
        compactView: false,
        pageNumber: 1,
        selectedSortTerm: "",
        sortByOptions: constants.SORT_BY_OPTIONS,
        inStockFilter: false,
    }),
    created() {
        this.pageNumber = this.initialPageNumber || 1;
        this.compactView = this.tmsCompactViewDefaultValue === "true" || this.tmsCompactViewDefaultValue === true;

        // NOTE: The parent (PartDiagramPage) triggers the initial search via
        // getCategoryParts / loadOemManualsDiagram in onSearchComplete, so we
        // intentionally do NOT call performPartSearch here to avoid a duplicate
        // API call on first render.
    },
    watch: {
        /** Persist compact-view preference whenever the user toggles it. */
        compactView(newVal: boolean) {
            this.$store.commit("SET_TMS_COMPACT_VIEW", newVal);
        },
        /** Reset to page 1 when new search results arrive (OemManuals client-side paging). */
        searchResults() {
            if (this.isDiagramSearch) {
                this.pageNumber = 1;
            }
        }
    },
    methods: {
        /** Bubble the hasAltParts event to the parent page. */
        HasAltParts(emittedHasAlts: boolean) {
            this.$emit("hasAltParts", emittedHasAlts);
        },

        /** Forward the facet-update event to the parent page. */
        UpdateFacets() {
            this.$emit("updateFacets");
        },

        /** Forward the redirect-to-truck-select event to the parent page. */
        redirectToTruckSelect() {
            this.$emit("redirectToTruckSelect");
        },

        /** Triggers a new part search for the given page. In OemManuals mode,
         *  pagination is client-side so we just update the page number.
         *  In TMS mode, emits to the parent for a server-side fetch. */
        performPartSearch(page: number) {
            this.pageNumber = page;
            if (this.isDiagramSearch) {
                // Client-side pagination — displayedParts computed will react
                return;
            }
            if (this.selectedSortTerm === "") {
                this.selectedSortTerm = this.partSearchDefaultSortValue === "" ? "salesVolumeDesc" : this.partSearchDefaultSortValue;
            }
            const selectedSortOption = this.sortByOptions.find((option: any) => option.value === this.selectedSortTerm);
            this.$emit("partSearch", selectedSortOption?.sortKey, selectedSortOption?.sortDirection, this.pageNumber, this.inStockFilter);
        },

        /** Builds a URL with the given category set as a query parameter. */
        getCategoryUrl(category: string): string {
            const url = new URL(window.location.href);
            url.searchParams.set("Category", category);
            return url.pathname + url.search;
        }
    }
});
